/**
 * @fileoverview Component Organism for rendering the chat window in the Inbox area.
 * Manages the visualization of messages, paginated scroll, lightbox of images and optimization of renders.
 * Strictly follows Atomic Design and separation of layers (Data/UI).
 *
 * @module AreaChatWindowMC
 * @category Component - Organism
 * @since 2.0.0
 * @version 3.2.0 - Complete JSDoc, typedefs, examples and cross-references
 *
 * @typedef {Object} AreaChatWindowMCProps
 * @description Props of the AreaChatWindowMC component (organism of chat)
 * @property {string|number} conversationId - Active conversation ID
 * @property {Object} [conversation] - Conversation object (optional, if already loaded)
 * @property {Array<Object>} [messages] - List of messages (optional, if already loaded)
 * @property {Object} [pagination] - Pagination object (optional)
 * @property {boolean} [loading] - Loading state (optional)
 *
 * @typedef {Object} Message
 * @description Individual message object in the chat
 * @property {string|number} id - Unique message ID
 * @property {string} direction - 'inbound' | 'outbound'
 * @property {string} contentType - Content type ('text', 'image', etc.)
 * @property {string} contentMediaType - Media type ('image', 'audio', etc.)
 * @property {boolean} [isNote] - If it is an internal note
 * @property {boolean} [isOrderNote] - If it is an order note
 * @property {boolean} [isMiaEvent] - If it is a MIA event
 * @property {boolean} [isBuyIntention] - If it is a buy intention
 * @property {boolean} [isGeneratedOrder] - If it is a generated MIA order
 * @property {boolean} [isHumanHelp] - If it is a human help message
 * @property {boolean} [isOrderError] - If a MIA order attempt failed
 * @property {boolean} [isPaymentLink] - If MIA sent a payment link
 * @property {boolean} [showError] - If it should show error visual
 * @property {Object} [repliedMessage] - Message to reply to (if applicable)
 * @property {string|number} [identifier] - Alternative identifier
 *
 * @component AreaChatWindowMC
 * @category Component - Organism
 *
 * @description
 * **Features:**
 * - Renders list of messages with infinite scroll and pagination
 * - Integrated image lightbox
 * - Optimization of performance with memoization and decoupled hooks
 * - Follows Atomic Design: Organism, uses molecules and atoms
 *
 * **Atomic Design:**
 * - **Level:** Organism
 * - **Composition:** Combines data/UI hooks, GalleryLightboxMC, LoadingMC components
 * - **Dependencies:**
 *   - useAreaChatWindowData (data layer)
 *   - useAreaChatWindowMessagesLoader (data layer)
 *   - useAreaChatWindowUI (ui layer)
 *   - usePaginatedScrollUI (ui layer)
 *   - useAreaChatWindowLightboxUI (ui layer)
 *   - useInboxChatGalleryImages (ui layer)
 *   - GalleryLightboxMC (global component)
 *   - LoadingMC (global component)
 *
 * **Props Interface:**
 * ```javascript
 * {
 *   conversationId: string|number,
 *   conversation?: Object,
 *   messages?: Array<Object>,
 *   pagination?: Object,
 *   loading?: boolean
 * }
 * ```
 *
 * @param {AreaChatWindowMCProps} props - Properties of the component
 *
 * @returns {JSX.Element} Rendered JSX element of the chat window
 *
 * @example
 * // Basic usage in Inbox
 * <AreaChatWindowMC conversationId={convId} />
 *
 * @see {@link ../../hooks/inbox/data/useAreaChatWindowData.js} Hook of data
 * @see {@link ../../hooks/inbox/ui/useAreaChatWindowUI.js} Hook of UI
 * @see {@link ../../hooks/shared/ui/usePaginatedScrollUI.js} Hook of scroll
 * @see {@link ../../hooks/inbox/ui/useInboxChatGalleryImages.js} Gallery images from messages
 * @see {@link ../../components/GalleryLightboxMC.js} Lightbox of images
 *
 * @since 2.0.0
 * @version 3.2.0 - Complete JSDoc, typedefs, examples and cross-references
 */
import React, {
  memo,
  useMemo,
  useCallback,
  useState,
  useEffect,
  Suspense
} from "react";
import {
  MiaSourcesResponseMessageMC,
  MessageMenuMC,
  MessageReactionMC,
  PostPreviewMC,
  MessageErrorDetailCardMC
} from "./components";
import { useTraceUpdate } from "../../hooks/shared/useDebugRenders";
import useAreaChatWindowData from "../../hooks/inbox/data/useAreaChatWindowData";
import useAreaChatWindowMessagesLoader from "../../hooks/inbox/data/useAreaChatWindowMessagesLoader";
import useAreaChatWindowUI from "../../hooks/inbox/ui/useAreaChatWindowUI";
import useAreaChatWindowLightboxUI from "../../hooks/inbox/ui/useAreaChatWindowLightboxUI";
import useInboxChatGalleryImages from "../../hooks/inbox/ui/useInboxChatGalleryImages";
import renderMessage from "./helpers/renderMessage";
import GalleryLightboxMC from "../../components/GalleryLightboxMC";
import LoadingMC from "../../components/LoadingMC";
import usePaginatedScrollUI from "../../hooks/shared/ui/usePaginatedScrollUI";
import ConsolidationIndicatorMC from "./components/ConsolidationIndicatorMC";
import { sharingInformationService } from "../../../services/sharingInformationService";

const AreaChatWindowMC = memo((props) => {
  /**
   * Hook Data Layer for getting conversation and messages data.
   * @type {{ conversation: Object, messages: Array<Object>, loading: boolean, pagination: Object }}
   */
  const {
    conversation,
    messages,
    loading,
    pagination
  } = useAreaChatWindowData(props);

  /**
   * Hook Data Layer for loading paginated messages.
   * @type {function}
   */
  const loadMessages = useAreaChatWindowMessagesLoader();

  /**
   * Hook UI Layer for paginated scroll decoupled.
   * @type {function}
   */
  const { containerRef, handleScroll } = usePaginatedScrollUI({
    loading,
    pagination,
    direction: 'up',
    onNextPage: (nextPage) => loadMessages(conversation, nextPage)
  });

  /**
   * Hook UI Layer for refs and write permissions.
   * @type {Object}
   */
  const { messagesEndRef, scrollToBottom } = useAreaChatWindowUI({
    messages,
    conversation,
    pagination,
    containerRef
  });

  const [consolidationData, setConsolidationData] = useState({
    isConsolidating: false,
    timeLeft: 0,
    phraseCount: 0,
    consolidatedText: ''
  });

  useEffect(() => {
    const subscription$ = sharingInformationService.getSubject();
    const subscription = subscription$.subscribe((data) => {
      if (data.consolidationData) {
        setConsolidationData(data.consolidationData);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (consolidationData.isConsolidating) {
      scrollToBottom();
    }
  }, [consolidationData.isConsolidating, consolidationData.consolidatedText, scrollToBottom]);

  /**
   * Hook UI Layer for management of lightbox of images.
   * @type {{ lightboxOpen: boolean, selectedImageId: string|number, openLightbox: function, closeLightbox: function }}
   */
  const {
    lightboxOpen,
    selectedImageId,
    openLightbox,
    closeLightbox
  } = useAreaChatWindowLightboxUI();

  const galleryImages = useInboxChatGalleryImages(messages);

  /**
   * Hook for debug to track re-renders and changes of props.
   * @see ../../hooks/shared/useDebugRenders
   */
  useTraceUpdate('AreaChatWindowMC', {
    conversationId: conversation?.id,
    platform: conversation?.platform,
    messagesCount: messages?.length || 0,
    loading,
    paginationCurrentPage: pagination?.current_page,
    paginationHasNext: pagination?.has_next_page,
    lightboxOpen,
    selectedImageId
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  /**
   * Memoize the flex classes for each message direction.
   * @function flexDivClasses
   * @private
   * @returns {Object} CSS classes for inbound/outbound
   */
  const flexDivClasses = useMemo(() => ({
    outbound: 'tw-text-gray-700 tw-break-words tw-flex tw-items-center tw-flex-row-reverse',
    inbound: 'tw-text-gray-700 tw-break-words tw-flex tw-items-center'
  }), []);

  /**
   * Calculate the CSS classes for the container of each message.
   *
   * @function divClasses
   * @memberof AreaChatWindowMC
   * @param {Object} message - Message to render
   * @returns {string} CSS classes for the message
   *
   * @example
   * const classes = divClasses(message);
   */
  const divClasses = useCallback((message) => {
    // `isGeneratedOrder`, not `isGeneratedMiaOrder`: the transform in
    // useAreaChatWindowData emits the former, so generated-order cards were
    // falling through to the yellow note background.
    const isMiaInternalMessage = message.isBuyIntention || message.isGeneratedOrder
      || message.isHumanHelp || message.isOrderError || message.isPaymentLink;

    if (message.isMiaClientData || message.isMiaAssignment) return 'tw-w-full';

    let classes = `tw-min-w-[35%] tw-max-w-[75%] tw-text-gray-950 tw-relative tw-break-words tw-inline-block tw-align-top tw-rounded-[14px] tw-text-sm tw-font-normal tw-m-0 tw-p-3 tw-shadow tw-shadow-gray-200 ${message.direction === 'outbound' ? 'tw-bg-[#D9FDD3]' : ''}`;

    if (['voice', 'audio'].includes(message.contentMediaType)) {
      classes += ' tw-bg-gray-100 !tw-min-w-[50%]';
    }

    if (['image', 'sticker'].includes(message.contentMediaType)) {
      classes += ' tw-bg-[#E4E4E4] !tw-p-0 !tw-w-auto';
    }

    if (message.contentMediaType === 'video') {
      classes += ' tw-bg-[#E4E4E4] !tw-p-0 !tw-w-full !tw-max-w-[320px]';
    }

    if (message.contentType === 'location') {
      classes += ' !tw-p-0';
    }

    if (message.isNote === true && !isMiaInternalMessage) {
      classes += ' tw-bg-yellow-100';
    }

    if (message.isOrderNote === true) {
      classes += ' !tw-p-0 !tw-mt-4 !tw-bg-white';
    }

    // If there is no tw-bg- class defined, add tw-bg-white
    if (!/\stw-bg-\S+/.test(classes)) {
      classes += ' tw-bg-white';
    }

    if (message.isMiaEvent) {
      classes += ' !tw-p-0 !tw-max-w-[100%] !tw-w-full !tw-bg-transparent !tw-shadow-none !tw-rounded-none';
    }

    if (isMiaInternalMessage) {
      classes += ' tw-bg-white';
    }

    return classes;
  }, []);

  /**
   * Calculate the CSS classes for the replied message (reply).
   *
   * @function divRepliedClasses
   * @memberof AreaChatWindowMC
   * @param {Object} message - Replied message
   * @returns {string} CSS classes for the reply
   */
  const divRepliedClasses = useCallback((message) => {
    const isOutboundImageBubble = message.direction === 'outbound'
      && (message.componentType === 'ImageMessageMC'
        || message.contentType === 'image'
        || message.contentMediaType === 'image');

    let classes = isOutboundImageBubble
      ? 'tw-opacity-70 tw-mx-2 tw-mt-2 tw-mb-2 tw-rounded-[10px] tw-px-2.5 tw-py-2 tw-overflow-hidden'
      : 'tw-opacity-70 tw-rounded-s tw-py-2 tw-px-0';

    if (message.direction === 'outbound') {
      classes += ' !tw-bg-[#d1f4cc]';
    } else {
      classes += ' !tw-bg-[#FAF9F6]';
    }

    return classes;
  }, []);

  /**
   * Memoize the rendering of the list of messages.
   *
   * @function renderedMessages
   * @memberof AreaChatWindowMC
   * @returns {JSX.Element[]} List of rendered message elements
   */
  const renderedMessages = useMemo(() => (
    messages
      .filter((msg) => {
        if (msg.componentType !== 'PostMessageMC') return true;
        // El preview de TikTok se renderiza desde platformSpecificData (video del comentario)
        const hasTikTokPreview = conversation?.platform === 'tik_tok_comments'
          && !!(conversation?.platformSpecificData?.video_thumbnail_url
            || conversation?.platformSpecificData?.video_caption);
        // Ocultar mensajes de tipo post si ya estamos mostrando el preview en el header
        if (conversation?.facebookPost || hasTikTokPreview) {
          return false;
        }
        return true;
      })
      .map((message, idx) => (
        <div
          key={`${message.id}-${message.identifier}`}
          className="tw-flex tw-flex-col tw-py-2 tw-px-1 tw-relative"
        >
          <div className={flexDivClasses[message.direction]}>
            <div className={`${divClasses(message)} tw-relative tw-group`}>
              <MessageMenuMC message={message} conversation={conversation} />
              {message.repliedMessage && (
                <div className={divRepliedClasses(message)}>
                  {renderMessage({
                    conversation,
                    message: message.repliedMessage,
                    onImageClick: openLightbox
                  })}
                </div>
              )}
              {renderMessage({
                conversation,
                message,
                onImageClick: openLightbox
              })}
              <MessageReactionMC message={message} />
            </div>
          </div>
          {message.direction === 'outbound' && message.miaSourcesResponse && (
            <div className={flexDivClasses[message.direction]}>
              <MiaSourcesResponseMessageMC
                message={message}
                sources={message.miaSourcesResponse}
              />
            </div>
          )}
          {message.direction === 'outbound' && message.errorDetails && (
            <MessageErrorDetailCardMC errorDetails={message.errorDetails} />
          )}
        </div>
      ))
  ), [
    messages,
    conversation,
    flexDivClasses,
    divClasses,
    divRepliedClasses,
    openLightbox
  ]);

  /**
   * Memoize the rendering of the post preview for comment conversations.
   *
   * @function renderedPostPreview
   * @memberof AreaChatWindowMC
   * @returns {JSX.Element|null} Post preview element or null
   */
  const renderedPostPreview = useMemo(() => (
    <PostPreviewMC conversation={conversation} />
  ), [conversation]);

  return (
    <Suspense fallback={<div className="loading" />}>
      <div
        ref={containerRef}
        className="tw-wrapper tw-flex-1 tw-h-0 tw-min-h-0 tw-min-w-0 tw-overflow-y-auto tw-p-4 !tw-bg-[#F9F7F5]"
        tabIndex={-1}
        aria-label="Chat messages"
        onScroll={handleScroll}
      >
        <LoadingMC loading={loading} />
        {renderedPostPreview}
        {renderedMessages}
        {consolidationData.isConsolidating && (
          <ConsolidationIndicatorMC
            isConsolidating={consolidationData.isConsolidating}
            timeLeft={consolidationData.timeLeft}
            phraseCount={consolidationData.phraseCount}
            charCount={consolidationData.consolidatedText ? consolidationData.consolidatedText.length : 0}
            consolidatedText={consolidationData.consolidatedText}
          />
        )}
        <div ref={messagesEndRef} />
        {lightboxOpen && (
          <GalleryLightboxMC
            images={galleryImages}
            selectedId={selectedImageId}
            onCloseRequest={closeLightbox}
          />
        )}
      </div>
    </Suspense>
  );
});

/**
 * Display name for debug and development tools.
 */
AreaChatWindowMC.displayName = "AreaChatWindowMC";

export default AreaChatWindowMC;
